import { useTranslation } from "react-i18next";

import { Button, Tag } from "@/components/ui";
import type { CallDraft } from "./model";
import { blockerFor } from "./model";
import type { ReceptionCompany } from "./queries";

/**
 * What every row of the capture window needs from the page above it.
 *
 * One object rather than six props threaded through four components: the rows
 * differ in what they show and are identical in what they can do.
 */
export type Capture = {
  draft: CallDraft;
  /** Who reception picked in *Asignar a*, if anyone yet. */
  agent: { id: number; name: string } | null;
  /** A call is being written; nothing else may be pressed meanwhile. */
  busy: boolean;
  assign: (company: ReceptionCompany, contact: { id: number }) => void;
  addContact: (company: ReceptionCompany) => void;
  /** Companies whose contact list is open, wherever on the screen they appear. */
  expanded: ReadonlySet<number>;
  toggle: (companyId: number) => void;
};

/** Whose the account is — the one fact that decides where the call goes. */
export function OwnerTag({ company }: { company: ReceptionCompany }) {
  const { t } = useTranslation();
  return company.current_agent === null ? (
    <Tag tone="accent">{t("reception.unassigned")}</Tag>
  ) : (
    <Tag>{t("reception.assignedTo", { agent: company.current_agent_name })}</Tag>
  );
}

/**
 * The button that hands the call over, for this company and this person.
 *
 * It says where the call will go before it is pressed. On an account somebody
 * holds that is its holder, whatever *Asignar a* says — the server gives it to
 * them either way, and a button that named somebody else would be lying.
 */
export function AssignButton({
  capture,
  company,
  contact,
}: {
  capture: Capture;
  company: ReceptionCompany;
  contact: { id: number };
}) {
  const { t } = useTranslation();
  const blocker = blockerFor(capture.draft, capture.agent?.id ?? null, company);
  const label =
    company.current_agent !== null
      ? t("reception.assignCall", { agent: company.current_agent_name })
      : capture.agent
        ? t("reception.assignToPicked", { agent: capture.agent.name })
        : t("reception.assignToAgent");

  return (
    <Button
      variant="primary"
      className="px-3 py-1.5 text-sm"
      disabled={blocker !== null || capture.busy}
      title={blocker ? t(`reception.blocked.${blocker}`) : undefined}
      onClick={() => capture.assign(company, contact)}
    >
      {label}
    </Button>
  );
}

/** A person, and the ways the book has to reach them. */
export function PersonLine({
  name,
  jobTitle,
  phones,
  emails,
  disabled,
}: {
  name: string;
  jobTitle: string;
  phones: readonly string[];
  emails: readonly string[];
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const reach = [...phones, ...emails].join(" · ");
  return (
    <div className={`min-w-0 ${disabled ? "opacity-60" : ""}`}>
      <p className="flex flex-wrap items-baseline gap-x-2 font-medium">
        {name}
        {jobTitle ? (
          <span className="text-xs font-normal text-mute-600">{jobTitle}</span>
        ) : null}
        {disabled ? <Tag tone="outline">{t("reception.disabled")}</Tag> : null}
      </p>
      {reach ? <p className="text-xs break-all text-mute-600">{reach}</p> : null}
    </div>
  );
}
